import { templateDictionaryItems } from "@/lib/data/static/templateDictionaryItems";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);

const FORMAT_TOKENS = new Set([
  "upperCase",
  "lowerCase",
  "titleCase",
  "plural",
  "possessive",
  "short",
  "long",
]);

const applyFormatToken = (format: string, value: string) => {
  switch (format) {
    case "upperCase":
      return value.toUpperCase();
    case "lowerCase":
      return value.toLowerCase();
    case "titleCase":
      return value
        .split(" ")
        .filter(Boolean)
        .map(
          (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
        )
        .join(" ");
    case "plural":
      return value.endsWith("s") ? value : `${value}s`;
    case "possessive": {
      const words = value.split(" ");
      const lastWord = words[words.length - 1];
      words[words.length - 1] = lastWord.endsWith("s")
        ? `${lastWord}'`
        : `${lastWord}'s`;
      return words.join(" ");
    }
    case "short":
      return dayjs.utc(value).format("M/D/YYYY").toString();
    case "long":
      return dayjs.utc(value).format("MMMM D, YYYY").toString();
    default:
      return value;
  }
};

export const parseSignatureRequestVariable = (variable: string) => {
  const [base, ...rest] = variable.split("_");
  let format: string | undefined;
  let instance: number | undefined;
  const aliasParts: string[] = [];

  rest.forEach((part) => {
    if (!Number.isNaN(Number(part))) {
      if (instance === undefined) {
        instance = Number(part);
      }
      return;
    }

    if (!format && FORMAT_TOKENS.has(part)) {
      format = part;
      return;
    }

    aliasParts.push(part);
  });

  return {
    baseVariable: base,
    alias: aliasParts.length > 0 ? aliasParts.join("_") : undefined,
    format,
    instance,
  };
};

export const getBaseKeyForSignatureRequestVariable = (variable: string) => {
  const parsed = parseSignatureRequestVariable(variable);
  const aliasPart = parsed.alias ? `_${parsed.alias}` : "";
  const instancePart = parsed.instance ? `_${parsed.instance}` : "";
  return `${parsed.baseVariable}${aliasPart}${instancePart}`;
};

const formatVariableValue = (value: string, variable: string) => {
  if (!value) return value;

  const { format } = parseSignatureRequestVariable(variable);
  if (!format) return value;

  return applyFormatToken(format, value);
};

const formatMergePayloadValue = (value: string, variable: string) => {
  if (!value) return value;

  const { baseVariable } = parseSignatureRequestVariable(variable);
  const templateItem = templateDictionaryItems.find(
    (item) => item.variable === baseVariable,
  );

  if (!templateItem?.lockedDisplayAsMultiline) {
    return value;
  }

  return value.replace(/\r?\n/g, "<br>");
};

export const buildSignatureRequestMergeInputs = (
  mappableVariables: string[],
  mappedVariableValues: Record<string, string>,
) =>
  mappableVariables.reduce<Record<string, string>>((acc, variable) => {
    const { baseVariable } = parseSignatureRequestVariable(variable);
    const baseKey = getBaseKeyForSignatureRequestVariable(variable);
    let value = "";
    const hasOwn = (key: string) =>
      Object.prototype.hasOwnProperty.call(mappedVariableValues, key);

    if (hasOwn(variable)) {
      value = mappedVariableValues[variable];
    } else if (hasOwn(baseKey)) {
      value = mappedVariableValues[baseKey];
    } else if (hasOwn(baseVariable)) {
      value = mappedVariableValues[baseVariable];
    }

    if (value === "") {
      return acc;
    }

    acc[variable] = formatMergePayloadValue(
      formatVariableValue(value, variable),
      variable,
    );

    return acc;
  }, {});
